import React, { Component } from "react";
import { connect } from "react-redux";
import { components } from "react-select";

import { useKeyboardHandler } from "./KeyboardEventContext";

import {
	closeGlobalDialog,
	globalDialogSubmit,
	globalDialogValidate,
} from "../actions/globalEditDialogActions";
import { ButtonWithIcon } from "./ButtonWithIcon";
import { LabeledTextInput } from "./TextInput";

import "./EditDialog.css";
import { LabeledDropDownSelect } from "./DropDownSelect";
import { LabeledTextArea } from "./TextArea";

import { SmallNoPaddingIconButton } from "./ButtonWithIcon";
import { ClioMatterSelector } from "./ClioMatterSelector";
import { SmartFileMatterSelector } from "./SmartEmailFiling/SmartFileMatterSelector.js";

import { deleteExternalEmailAddressForUser } from "../actions/shareActions";
import {
	toggleMandatoryProfilingDialogBulkProcessing,
	togglePasswordProtectShare,
} from "../actions/browserLocalstorageActions";
import { Checkbox } from "@mantine/core";
import BulkMatterPermissions from "./BulkMatterPermissions";
import PermanentDeleteConfirmationModal from "./TrashBin/PermanentDeleteConfirmationModal";
import PermanentDeleteJobDetails from "./TrashBin/PermanentDeleteJobDetails.js";
import MandatoryProfilingDialog from "./mandatoryProfiling/dialog/MandatoryProfilingDialog";
import NewDocumentWithProfilingDialog from "./mandatoryProfiling/dialog/NewDocumentWithProfilingDialog";
import { StorageDetails } from "./Homepage/StorageDetails.js";
import UnsavedChangesConfirmation from "./UnsavedChangesConfirmation";
import {
	LexWorkplaceCheckIcon,
	LexWorkplaceXIcon,
} from "./LexWorkplaceIcons.js";
import { IconSend, IconTrash } from "@tabler/icons-react";
import PreBox from "./PreBox.js";
import { SimpleSwitch } from "./AsyncSwitch.js";
import { increaseMonthlyOCR } from "../actions/homepageActions.js";

function EditDialogKeyboardHandler({ onClose }) {
	useKeyboardHandler(["esc"], onClose, { forceCloseDialog: true });
	return null;
}

class EditDialog extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			value: "",
			textAreaContent: "",
			recipients: null,
			convertToPDFAsNewVersion: true,
			mandatoryProfilingData: {},
			mandatoryProfilingValid: false,
			newDocumentDialog: {
				filename: "",
				documentType: null,
				documentStatus: null,
				documentTags: [],
			},
		};
		this.formRef = React.createRef();
		this.handleChange = this.handleChange.bind(this);
		this.submit = this.submit.bind(this);
		this.externalShareComponent = this.externalShareComponent.bind(this);

		this.inputFieldComponent = this.inputFieldComponent.bind(this);
		this.switchComponentType = this.switchComponentType.bind(this);
		this.copyValueComponent = this.copyValueComponent.bind(this);
		this.extraButtonField = this.extraButtonField.bind(this);
		this.handleMandatoryProfilingSettingsChange =
			this.handleMandatoryProfilingSettingsChange.bind(this);
		this.handleMandatoryProfilingValidation =
			this.handleMandatoryProfilingValidation.bind(this);
		this.newDocumentWithProfilingComponent =
			this.newDocumentWithProfilingComponent.bind(this);
	}
	static getDerivedStateFromProps(props, state) {
		if (state.loading && !props.loading) {
			const newState = {
				loading: false,
				textAreaContent: "",
			};
			return newState;
		} else {
			return null;
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.dialogOpen !== prevProps.dialogOpen) {
			this.setState({
				value: this.props.value || "",
				textAreaContent: "",
				recipients: null,
				mandatoryProfilingData: {},
				passwordProtectShare: this.props.passwordProtectShare,
			});

			if (this.props.dialogOpen) {
				// Block body scroll when dialog opens
				document.body.style.overflow = "hidden";

				if (this.formRef.current && this.formRef.current.textInput) {
					//if the dialog is newly open, heavy handedly grab focus after 5ms
					setTimeout(() => {
						this.formRef.current.textInput.focus();
					}, 5);
				}
			} else {
				// Restore body scroll when dialog closes
				document.body.style.overflow = "";
			}
		}
	}

	componentWillUnmount() {
		// Always restore body scroll when dialog component unmounts
		document.body.style.overflow = "";
	}

	handleChange(key, value) {
		this.setState({ [key]: value }, () => {
			this.props.globalDialogValidate(this.state);
		});
	}
	isOverLimit() {
		const { maxLength } = this.props;
		return maxLength && (this.state.value || "").length > maxLength;
	}
	submit() {
		if (this.props.canSubmit && !this.isOverLimit()) {
			this.props.globalDialogSubmit &&
				this.props.globalDialogSubmit(this.state);
		}
	}

	handleMandatoryProfilingSettingsChange(profilingData) {
		this.setState({ mandatoryProfilingData: profilingData });
	}

	handleMandatoryProfilingValidation(isValid) {
		this.setState({ mandatoryProfilingValid: isValid }, () => {
			this.props.globalDialogValidate(this.state);
		});
	}

	newDocumentWithProfilingComponent() {
		const config = this.props.newDocumentProfilingConfig || {};
		const {
			requiredFields = [],
			availableOptions = {},
			extension,
			stronglySuggestProfiling = false,
		} = config;

		return (
			<NewDocumentWithProfilingDialog
				stronglySuggestProfiling={stronglySuggestProfiling}
				requiredFields={requiredFields}
				availableOptions={availableOptions}
				extension={extension}
				defaultFilename={this.props.value || ""}
				placeholder={this.props.placeHolder || this.props.label}
				onChange={(data) => {
					// Update state with all the data from the component
					this.setState(
						{
							value: data.filename,
							newDocumentDialog: {
								filename: data.filename,
								documentType: data.documentType,
								documentStatus: data.documentStatus,
								documentTags: data.documentTags,
							},
						},
						() => {
							this.props.globalDialogValidate(this.state);
						}
					);
				}}
				onValidationChange={() => {
					// This is handled by the parent onChange triggering globalDialogValidate
				}}
				onEnterKey={this.submit}
				userRights={this.props.userRights}
			/>
		);
	}

	externalShareComponent() {
		const OptionWithDeleteButton = (props) => {
			const { isFocused, data } = props;
			return (
				<span style={{}}>
					<div
						style={{
							position: "absolute",
							right: 12,
							paddingTop: 7,
						}}
					>
						{isFocused ? (
							<div>
								<SmallNoPaddingIconButton
									title="delete email address from my saved list"
									onClick={() =>
										this.props.deleteExternalEmailAddressForUser(
											data.value
										)
									}
								>
									<IconTrash
										size="1rem"
										style={{ verticalAlign: "sub" }}
									/>
								</SmallNoPaddingIconButton>
							</div>
						) : null}
					</div>
					<components.Option {...props} />
				</span>
			);
		};
		return (
			<div>
				<div style={{ width: "100%" }}>
					<LabeledDropDownSelect
						label="Recipient(s)"
						isMulti
						name="recipients"
						isCreatable
						isEmail={true}
						isAsync
						styles={{
							option: (base) => ({
								...base,
								height: "100%",
							}),
						}}
						options={
							this.props.emailAddressListLoading
								? []
								: this.props.externalEmailAddresses.map(
										(e) => ({
											label: e,
											value: e,
										})
									)
						}
						isLoading={this.props.emailAddressListLoading}
						defaultValue={[]}
						disable={this.canSubmit}
						handler={this.handleChange}
						components={{ Option: OptionWithDeleteButton }}
					/>
				</div>
				<div>
					<LabeledTextArea
						name={"textAreaContent"}
						label={"Message"}
						onChange={this.handleChange}
						defaultValue={this.props.textAreaContent}
					/>
				</div>
				<div>
					{this.props.externalShareIsPasswordProtected ? (
						<div>
							<div
								style={{
									fontWeight: 600,
									marginBottom: 4,
								}}
							>
								Password Protect Share
							</div>
							<div style={{ fontSize: 12, color: "#666" }}>
								Your firm requires all shared files to be
								password-protected. A password will be generated
								automatically and shown on the Share Detail page
								once the Documents have been sent, where you can
								find and provide it to your recipient.
							</div>
						</div>
					) : (
						<>
							<SimpleSwitch
								label="Password Protect Share"
								labelVariant="div"
								variant="formInput"
								switchValue={this.state.passwordProtectShare}
								handleSwitchValueChange={() => {
									// Intentionally does not call handleChange/globalDialogValidate:
									// this toggle doesn't affect recipient validity, and re-running
									// validation here would prematurely surface the "email required"
									// error before the user has touched the recipients field.
									this.setState({
										passwordProtectShare:
											!this.state.passwordProtectShare,
									});
									this.props.togglePasswordProtectShare();
								}}
							/>
							{this.state.passwordProtectShare ? (
								<div style={{ fontSize: 12, color: "#666" }}>
									<div>{`A password will be auto-generated and will be required for recipients to view/download the files. You can find the password on the share details page after the Share is created.`}</div>
									<div
										style={{ marginTop: 8 }}
									>{`Note: The Message will not be password-protected -  it will be visible to anyone with the share link.`}</div>
								</div>
							) : null}
						</>
					)}
				</div>
			</div>
		);
	}

	inputFieldComponent() {
		return (
			<LabeledTextInput
				label={this.props.textAreaLabel || ""}
				name="value"
				formRef={this.formRef}
				autoFocus
				placeholder={this.props.placeHolder || this.props.label}
				defaultValue={this.props.value}
				onChange={this.handleChange}
				onEnterKey={this.submit}
				disabled={this.props.readOnly}
			/>
		);
	}
	textareaFieldComponent() {
		const { maxLength } = this.props;
		const charCount = (this.state.value || "").length;
		const overLimit = maxLength && charCount > maxLength;
		return (
			<>
				<LabeledTextArea
					label=""
					name="value"
					autoFocus
					rows={5}
					defaultValue={this.props.value}
					placeholder={this.props.placeHolder}
					onChange={this.handleChange}
				/>
				{maxLength ? (
					<div
						style={{
							textAlign: "right",
							fontSize: 12,
							color: overLimit ? "red" : "#666",
							marginTop: 2,
						}}
					>
						{charCount}/{maxLength}
					</div>
				) : null}
			</>
		);
	}
	copyValueComponent() {
		return (
			<div>
				<PreBox style={{ marginRight: 8 }}>{this.props.value}</PreBox>
				<button
					style={{ height: 39, borderRadius: 4 }}
					onClick={() => {
						navigator.clipboard.writeText(this.props.value);
						this.props.closeGlobalDialog();
					}}
				>
					Copy
				</button>
			</div>
		);
	}
	convertPDFTODocPopUpOptions() {
		return (
			<div>
				<ButtonWithIcon
					style={{
						whiteSpace: "pre-wrap",
						wordWrap: "break-word",
						display: "block",
					}}
					buttonText={`Create a New PDF Named "${this.props.value.newDocName}"`}
					className="blue"
					iconComponent={
						this.props.submitIcon === "send" ? (
							<IconSend
								size={16}
								style={{ verticalAlign: "middle" }}
							/>
						) : (
							<LexWorkplaceCheckIcon />
						)
					}
					handler={() => {
						this.setState(
							{
								convertToPDFAsNewVersion: false,
							},
							this.submit
						);
					}}
				/>
				<ButtonWithIcon
					style={{
						marginTop: 5,
						whiteSpace: "pre-wrap",
						wordWrap: "break-word",
						display: "block",
					}}
					buttonText={`Save this PDF as a New Version in "${this.props.value.fileName}"`}
					className="blue"
					disabled={
						this.props.value.isDocumentInUse ||
						this.props.value.isOriginalPdfNameInTrash
					}
					iconComponent={
						this.props.submitIcon === "send" ? (
							<IconSend
								size={16}
								style={{ verticalAlign: "middle" }}
							/>
						) : (
							<LexWorkplaceCheckIcon />
						)
					}
					handler={() => {
						this.setState(
							{
								convertToPDFAsNewVersion: true,
							},
							this.submit
						);
					}}
				/>

				<div style={{ marginTop: 10 }}>
					{this.props.value.isDocumentInUse ? (
						<div style={{ marginTop: 10 }}>
							{this.props.value.documentInUseMessage}
						</div>
					) : null}

					{this.props.value.isOriginalPdfNameInTrash ? (
						<div style={{ marginTop: 10 }}>
							{this.props.value.documentInTrashMessage}
						</div>
					) : null}
					<ButtonWithIcon
						handler={this.props.closeGlobalDialog}
						buttonText={this.props.cancelButtonLabel || "Cancel"}
						iconComponent={<LexWorkplaceXIcon />}
						style={{
							marginTop: 10,
						}}
					/>
				</div>
			</div>
		);
	}

	duplicateEmailsFoundOptions() {
		const { numNonDuplicates, numDuplicates, total } =
			this.props.value || {};
		return (
			<div>
				{numNonDuplicates > 0 ? (
					<ButtonWithIcon
						style={{
							whiteSpace: "pre-wrap",
							wordWrap: "break-word",
							display: "block",
						}}
						buttonText={`Just Upload the ${numNonDuplicates} Non-Duplicate${
							numNonDuplicates > 1 ? "s" : ""
						}`}
						className="blue"
						iconComponent={<LexWorkplaceCheckIcon />}
						handler={() => {
							this.setState(
								{ uploadNonDuplicatesOnly: true },
								this.submit
							);
						}}
					/>
				) : null}
				<ButtonWithIcon
					style={{
						marginTop: 5,
						whiteSpace: "pre-wrap",
						wordWrap: "break-word",
						display: "block",
					}}
					buttonText={`Upload All ${total} Email${
						total > 1 ? "s" : ""
					} (Including the ${numDuplicates} Duplicate${
						numDuplicates > 1 ? "s" : ""
					})`}
					className="blue"
					iconComponent={<LexWorkplaceCheckIcon />}
					handler={() => {
						this.setState(
							{ uploadNonDuplicatesOnly: false },
							this.submit
						);
					}}
				/>
				<div style={{ marginTop: 10 }}>
					<ButtonWithIcon
						handler={this.props.closeGlobalDialog}
						buttonText={this.props.cancelButtonLabel || "Cancel"}
						iconComponent={<LexWorkplaceXIcon />}
					/>
				</div>
			</div>
		);
	}
	switchComponentType() {
		if (this.props.type === "email") {
			return this.externalShareComponent();
		} else if (this.props.type === "clioDropdown") {
			return (
				<ClioMatterSelector
					name="clioMatterList"
					label={this.props.label}
					clioMattersList={this.props.clioMattersList}
					handler={this.handleChange}
				/>
			);
		} else if (this.props.type === "copyValue") {
			return this.copyValueComponent();
		} else if (this.props.type === "BulkMatterPermissions") {
			return (
				<BulkMatterPermissions
					{...this.props.bulkMatterPermissionsObject}
					onChange={this.handleChange}
				/>
			);
		} else if (this.props.type === "MandatoryProfiling") {
			return (
				<MandatoryProfilingDialog
					{...this.props.bulkUploadProfilingSettings}
					onChange={this.handleMandatoryProfilingSettingsChange}
					onValidationChange={this.handleMandatoryProfilingValidation}
					screenSize={this.props.screenSize}
					mandatoryProfilingDialogBulkProcessingExpanded={
						this.props
							.mandatoryProfilingDialogBulkProcessingExpanded
					}
					toggleMandatoryProfilingDialogBulkProcessing={
						this.props.toggleMandatoryProfilingDialogBulkProcessing
					}
					userRights={this.props.userRights}
				/>
			);
		} else if (this.props.type === "NewDocumentWithMandatoryProfiling") {
			return this.newDocumentWithProfilingComponent();
		} else if (this.props.type === "permanentDeleteConfirmationModal") {
			return (
				<PermanentDeleteConfirmationModal
					{...this.props.permanentDeleteConfirmationObject}
					onChange={this.handleChange}
				/>
			);
		} else if (this.props.type === "lxwMatterSelector") {
			return (
				<SmartFileMatterSelector
					smartFileAssociatedMatter={
						this.props.smartFileAssociatedMatter
					}
					allMattersInLexWorkplace={
						this.props.allMattersInLexWorkplace
					}
					handleChange={this.handleChange}
				/>
			);
		} else if (this.props.type === "permanentDeleteJobDetails") {
			return (
				<PermanentDeleteJobDetails
					{...this.props.permanentDeleteJobDetailsObject}
				/>
			);
		} else if (this.props.type === "storageDetails") {
			return (
				<StorageDetails
					{...this.props.storageDetailsObject}
					increaseMonthlyOCR={this.props.increaseMonthlyOCR}
				/>
			);
		} else if (this.props.type === "unsavedChangesConfirmation") {
			return <UnsavedChangesConfirmation />;
		} else {
			if (this.props.hideTextfield) {
				return null;
			} else if (this.props.useTextarea) {
				return this.textareaFieldComponent();
			} else {
				return this.inputFieldComponent();
			}
		}
	}

	extraButtonField() {
		if (this.props.type === "docTOPDF") {
			return this.convertPDFTODocPopUpOptions();
		} else if (this.props.type === "emailDuplicatesFound") {
			return this.duplicateEmailsFoundOptions();
		} else if (this.props.type === "ocrLimitWarning") {
			return (
				<div style={{ marginLeft: "-15px", marginTop: "5px" }}>
					<Checkbox
						size="xs"
						style={{ left: 4 }}
						onChange={(event) => {
							if (event.currentTarget.checked) {
								let timestamp = new Date().toJSON();
								window.localStorage.setItem(
									"ignoreOverOcrLimitWarning",
									timestamp
								);
							} else {
								window.localStorage.removeItem(
									"ignoreOverOcrLimitWarning"
								);
							}
						}}
					/>
					Ignore OCR credit warnings for the rest of the month
				</div>
			);
		}
	}
	render() {
		if (!this.props.dialogOpen) {
			return null;
		} else {
			if (this.state.loading) {
				return (
					<div className="EditDialogBlocker">
						<div className="EditDialog">
							<h1>
								Loading<span className="Loading">...</span>
							</h1>
						</div>
					</div>
				);
			} else {
				return (
					<div className="EditDialogBlocker">
						<div
							className={
								this.props.extraCSSClass
									? this.props.extraCSSClass
									: "EditDialog"
							}
						>
							<EditDialogKeyboardHandler
								onClose={() => {
									this.props.closeGlobalDialog();
								}}
							/>
							<div>
								<h3>{this.props.label}</h3>
								<div
									style={{
										marginBottom: this.props.mainText
											? 8
											: this.extraButtonField()
												? 2
												: 20,
										marginTop: 4,
										whiteSpace: "pre-wrap",
										wordWrap: "break-word",
										display: "block",
									}}
								>
									{this.props.mainText}
								</div>

								<div>
									<div style={{ display: "flex" }}>
										<div style={{ flexGrow: 1 }}>
											{this.switchComponentType()}
										</div>
										<div
											style={{
												marginLeft: 8,
												paddingTop: 8,
											}}
										>
											{this.props.suffix &&
												`.${this.props.suffix}`}
										</div>
									</div>
								</div>
							</div>
							{this.props.errorMessage ? (
								<div className="inlineErrorIndicator">
									{this.props.errorMessage}
								</div>
							) : null}

							<div style={{ marginTop: 10 }}>
								{this.props.hideCancelButton ? null : (
									<ButtonWithIcon
										handler={this.props.closeGlobalDialog}
										buttonText={
											this.props.cancelButtonLabel ||
											"Cancel"
										}
										iconComponent={<LexWorkplaceXIcon />}
										style={{ marginRight: 8 }}
									/>
								)}
								{this.props.readOnly ? null : (
									<ButtonWithIcon
										handler={this.submit.bind(this)}
										buttonText={
											this.props.submitButtonLabel
										}
										disabledHoverText={
											this.props
												.disabledHoverTextForSubmitButton
										}
										disabled={
											!this.props.canSubmit ||
											this.isOverLimit()
										}
										className={
											this.props.redSubmitButton
												? "redButton"
												: "blue"
										}
										iconComponent={
											this.props.submitIcon === "send" ? (
												<IconSend
													size={16}
													style={{
														verticalAlign: "middle",
													}}
												/>
											) : (
												<LexWorkplaceCheckIcon />
											)
										}
									/>
								)}
								<div style={{ marginTop: 5 }}>
									{this.extraButtonField()}
								</div>
							</div>
						</div>
					</div>
				);
			}
		}
	}
}
const mapStateToProps = (state) => {
	return {
		matterDetails: state.matter.matterDetails,
		loading: state.matter.newEditMatterLoading,
		dialogOpen: state.globalDialog.dialogOpen,
		label: state.globalDialog.label,
		mainText: state.globalDialog.mainText,
		value: state.globalDialog.value,
		canSubmit: state.globalDialog.canSubmit,
		errorMessage: state.globalDialog.errorMessage,
		submitIcon: state.globalDialog.submitIcon,
		placeHolder: state.globalDialog.placeHolder,
		textAreaLabel: state.globalDialog.textAreaLabel,
		submitButtonLabel: state.globalDialog.submitButtonLabel,
		cancelButtonLabel: state.globalDialog.cancelButtonLabel,
		readOnly: state.globalDialog.readOnly,
		suffix: state.globalDialog.suffix,
		hideTextfield: state.globalDialog.hideTextfield,
		clioMattersList: state.clioIntegration.clioMatterListData,
		clioMatterListStatus: state.clioIntegration.clioStatus,
		recipients: state.globalDialog.recipients,
		passwordProtectShare: state.globalDialog.passwordProtectShare,
		externalShareIsPasswordProtected:
			state.tenant.tenantFeatures.externalShareIsPasswordProtected,
		textAreaContent: state.globalDialog.textAreaContent,
		textAreaName: state.globalDialog.textAreaName,
		type: state.globalDialog.type,
		hideCancelButton: state.globalDialog.hideCancelButton,
		externalEmailAddresses: state.user.externalEmailAddresses,
		emailAddressListLoading: state.user.emailAddressListLoading,
		extraCSSClass: state.globalDialog.extraCSSClass,
		bulkMatterPermissionsObject:
			state.globalDialog.bulkMatterPermissionsObject,
		bulkUploadProfilingSettings:
			state.globalDialog.bulkUploadProfilingSettings,
		newDocumentProfilingConfig:
			state.globalDialog.newDocumentProfilingConfig,
		permanentDeleteConfirmationObject:
			state.globalDialog.permanentDeleteConfirmationObject,
		disabledHoverTextForSubmitButton:
			state.globalDialog.disabledHoverTextForSubmitButton,
		redSubmitButton: state.globalDialog.redSubmitButton,
		allMattersInLexWorkplace: state.user.allMatterNames,
		smartFileAssociatedMatter: state.globalDialog.smartFileAssociatedMatter,
		permanentDeleteJobDetailsObject:
			state.globalDialog.permanentDeleteJobDetailsObject,
		storageDetailsObject: state.globalDialog.storageDetailsObject,
		useTextarea: state.globalDialog.useTextarea,
		maxLength: state.globalDialog.maxLength,
		screenSize: state.global.size,
		mandatoryProfilingDialogBulkProcessingExpanded:
			state.browserLocalstorage
				.mandatoryProfilingDialogBulkProcessingExpanded,
		userRights: state.user.userRights,
	};
};
EditDialog.propTypes = {};

export default connect(mapStateToProps, {
	closeGlobalDialog,
	globalDialogSubmit,
	globalDialogValidate,
	deleteExternalEmailAddressForUser,
	toggleMandatoryProfilingDialogBulkProcessing,
	togglePasswordProtectShare,
	increaseMonthlyOCR,
})(EditDialog);
